import { action, comparer, computed, IReactionDisposer, makeObservable, override, reaction } from 'mobx';

import AppSettings from '../AppSettings';
import { ForceDownloadUrlBuilder } from '../configurator/ForceDownloadUrlBuilder';
import { Tab } from '../configurator/Tab';
import { IApiClient } from '../data/client';
import {
  AddProductToCartCommand,
  GetConfigurationByCodeQuery,
  GetProductFamilyQuery,
  GetStoreContextQueryResponse,
  ProductConfigurationMode,
  ProductFamilyData,
  ShoppingContext,
} from '../data/model';
import { ConfiguratorUrlBuilder } from '../errorHandling/ParseConfigurationCodeAndRedirectState';
import { WarningStateBase } from '../layout/Warnings/WarningStateBase';
import { ProductFamilyPageTranslation } from '../localization/SiteTranslation';
import { BasePageState } from '../shared/BasePageState';
import { assertUnreachable, AsyncCommand, IMemento, PageMetadata } from '../shared/common';
import { AssetsDownloaderState } from '../shared/components/AssetsDownloader/AssetsDownloaderState';
import { GalleryState } from '../shared/components/Gallery/GalleryState';
import { IConfigurationCodeResolutionService } from '../shared/ConfigurationCodeResolutionService';
import { IEventAggregator } from '../shared/EventAggregator';
import { ProductConfigurationQuery } from '../shared/hubSpot/ProductConfigurationQuery';
import { IImagePreloader } from '../shared/ImagePreloader';
import { INavigationService } from '../shared/NavigationService';
import { StatusCodes } from '../shared/StatusCodes';
import { StoreUrl } from '../shared/StoreUrl';
import { StoreState } from '../StoreState';
import ExternalConfiguratorModalState from './components/ExternalConfiguratorModal/ExternalConfiguratorModalState';
import ModularProductDetailsState from './components/ModularProductDetails/ModularProductDetailsState';
import ProductDetailsState from './components/ProductDetails/ProductDetailsState';
import { RelatedProductsState } from './components/RelatedProducts/RelatedProductsState';
import SuggestedConfigurationsState from './components/SuggestedConfigurations/SuggestedConfigurationsState';
import { ProductViewedEvent, RequestQuoteInitiatedEvent } from './ProductFamilyPageEvents';
import { ProductFamilyPageQueryConverter } from './ProductFamilyPageQueryConverter';

export interface IProductFamilyPageMemento extends IMemento {
  shoppingContext: ShoppingContext;
  storeResponse: GetStoreContextQueryResponse;
  data: ProductFamilyData;
}

export default class ProductFamilyPageState extends BasePageState<IProductFamilyPageMemento> {
  private imagePreloader: IImagePreloader;

  private client: IApiClient;
  private navigation: INavigationService;
  private eventAggregator: IEventAggregator;
  private shoppingContext: ShoppingContext;
  private storeResponse: GetStoreContextQueryResponse;
  private disposeActiveConfigurationReaction: IReactionDisposer;

  public data: ProductFamilyData;
  public translation: ProductFamilyPageTranslation;
  public imageGallery: GalleryState;
  public relatedProducts: RelatedProductsState;
  public productsAssets: AssetsDownloaderState;
  public modelDetails?: ProductDetailsState;
  public modularProductDetails?: ModularProductDetailsState;
  public externalConfiguratorModal?: ExternalConfiguratorModalState;

  familyId: string;
  suggestedConfigurations: SuggestedConfigurationsState;
  goToConfiguratorCommand: AsyncCommand;
  goToCheckoutCommand: AsyncCommand;
  askForPriceCommand: AsyncCommand;
  storeState: StoreState;
  productNotAvailableInCurrentStoreState: WarningStateBase<string>;

  constructor(
    storeState: StoreState,
    settings: AppSettings,
    familyId: string,
    client: IApiClient,
    navigation: INavigationService,
    imagePreloader: IImagePreloader,
    translation: ProductFamilyPageTranslation,
    codeResolver: IConfigurationCodeResolutionService,
    eventAggregator: IEventAggregator,
  ) {
    super(translation);

    makeObservable(this);

    this.storeState = storeState;
    this.familyId = familyId;
    this.imagePreloader = imagePreloader;

    this.translation = translation;
    this.client = client;
    this.navigation = navigation;
    this.eventAggregator = eventAggregator;

    this.suggestedConfigurations = new SuggestedConfigurationsState(codeResolver);
    const forceDownload = new ForceDownloadUrlBuilder(settings.functionsUrl);
    this.imageGallery = new GalleryState(forceDownload, 800, 600);
    this.relatedProducts = new RelatedProductsState();
    this.productNotAvailableInCurrentStoreState = new WarningStateBase<string>();

    this.productsAssets = new AssetsDownloaderState(
      client,
      navigation,
      translation.productModelDetailsSection.assetsDownloader,
    );

    this.goToConfiguratorCommand = new AsyncCommand(() => this.goToConfigurator());
    this.goToCheckoutCommand = new AsyncCommand(() => this.goToCheckout());
    this.askForPriceCommand = new AsyncCommand(() => this.askForPrice());

    this.onPageLoaded = this.onPageLoaded.bind(this);
    this.navigation.pageLoaded.subscribe(this.onPageLoaded);
  }

  private initialize(
    shoppingContext: ShoppingContext,
    storeResponse: GetStoreContextQueryResponse,
    data: ProductFamilyData,
  ) {
    this.shoppingContext = shoppingContext;
    this.storeResponse = storeResponse;
    this.data = data;

    if (
      data.configurationMode === ProductConfigurationMode.Modular ||
      data.configurationMode === ProductConfigurationMode.ModularExternal
    ) {
      this.modularProductDetails = new ModularProductDetailsState(
        this.data,
        this.translation.modelsSection.productInfo,
      );
    }

    if (data.configurationMode === ProductConfigurationMode.ModularExternal) {
      this.externalConfiguratorModal = new ExternalConfiguratorModalState(
        shoppingContext.languageCode,
        data.externalConfiguratorLink,
        this.translation.modularLayouts.configuratorModal,
      );
    }

    this.productsAssets.setAssets(data.name, data.assets);

    if (!this.hasModels && this.data.configurationMode === ProductConfigurationMode.Default) {
      this.showProductNotAvailableError();
      return;
    }

    this.modelDetails = new ProductDetailsState(
      this.data.models,
      this.imagePreloader,
      this.translation,
      this.data.listingIcons,
    );
    this.suggestedConfigurations.initialize(data.predefinedConfigurations);
    this.imageGallery.setImages(data.gallery);

    this.relatedProducts.initialize(data.relatedProducts, 4);

    const queryConverter = new ProductFamilyPageQueryConverter(this.navigation, this.modelDetails);
    queryConverter.initialize();

    // A model change also switches the predefined configuration; the reaction collapses both into one event
    this.disposeActiveConfigurationReaction = reaction(
      () => [
        this.modelDetails.modelSelector.active?.data.id,
        this.modelDetails.predefinedConfigurations.active?.data.code,
      ],
      () => this.notifyProductViewed(),
      { equals: comparer.structural },
    );
  }

  @action
  async onLoad(store: StoreState) {
    const previewMode = this.navigation.currentUrl.query.has('preview');
    const query = new GetProductFamilyQuery({
      familyId: this.familyId,
      shoppingContext: store.shoppingContext,
      previewMode: previewMode,
    });
    const response = await this.client.send(query);

    if (response.redirectToFamilyId) {
      this.navigation.redirect(
        StoreUrl.productPageUrl(response.redirectToFamilyId).toString(),
        StatusCodes.MovedPermanently,
      );
    }

    this.initialize(store.shoppingContext, store.storeResponse, response.productFamily);
  }

  async onLoadAdditionalData() {
    if (this.modelDetails) {
      await this.modelDetails.modelViewer.finishPreloadingVisualizationImages();
    }
  }

  getMemento(): IProductFamilyPageMemento {
    return {
      shoppingContext: this.shoppingContext,
      storeResponse: this.storeResponse,
      data: this.data,
    };
  }

  restoreMemento(memento: IProductFamilyPageMemento) {
    this.initialize(memento.shoppingContext, memento.storeResponse, memento.data);
  }

  public unload() {
    this.navigation.pageLoaded.unsubscribe(this.onPageLoaded);
    this.disposeActiveConfigurationReaction?.();
  }

  private onPageLoaded() {
    this.notifyProductViewed();
  }

  private notifyProductViewed() {
    // TODO: Handle modular product
    if (this.data?.configurationMode !== ProductConfigurationMode.Default || !this.modelDetails) {
      return;
    }

    this.eventAggregator.publish(
      new ProductViewedEvent(
        this.modelDetails.modelSelector.active.data,
        this.modelDetails.predefinedConfigurations.active?.data.code,
      ),
    );
  }

  async goToConfigurator(tab?: Tab): Promise<void> {
    const selectedConfiguration = this.modelDetails.predefinedConfigurations.active;
    if (!selectedConfiguration) {
      return Promise.resolve();
    }

    const builder = new ConfiguratorUrlBuilder(this.client);
    const configuratorUrl = await builder.buildFromCode(selectedConfiguration.data.code, undefined, tab);

    this.navigation.navigateTo(configuratorUrl);
    return Promise.resolve();
  }

  async askForPrice(): Promise<void> {
    const selectedConfiguration = this.modelDetails.predefinedConfigurations.active;
    const configurationCode = selectedConfiguration?.data.code;
    const product = this.modelDetails.modelSelector.active?.data;

    // TODO: Handle external modular products
    if (product) {
      this.eventAggregator.publish(
        new RequestQuoteInitiatedEvent(product, configurationCode, this.shoppingContext.currencyCode),
      );
    }

    const query = this.getRequestQuoteQuery(product?.code, configurationCode);
    this.navigation.navigateTo(StoreUrl.requestQuoteUrl(query));
    return Promise.resolve();
  }

  async showProductNotAvailableError(): Promise<void> {
    const store = this.storeResponse.storeContext.store;
    const storeName = store.displayName || store.name;
    this.productNotAvailableInCurrentStoreState.open(storeName);
  }

  async addToCart(): Promise<void> {
    const selectedConfiguration = this.modelDetails.predefinedConfigurations.active;
    const configurationCode = selectedConfiguration.data.code;
    const getConfigurationResponse = await this.client.send(
      new GetConfigurationByCodeQuery({ code: configurationCode }),
    );
    const configuration = getConfigurationResponse.productConfiguration;

    const addToCartCommand = new AddProductToCartCommand({
      shoppingContext: this.shoppingContext,
      productConfiguration: configuration,
    });

    await this.client.send(addToCartCommand);
  }

  async goToCheckout(): Promise<void> {
    this.goToConfigurator(Tab.Summary);
    // Todo: Bug with prices
    // await this.addToCart();

    // this.navigation.navigateTo(StoreUrl.checkoutUrl());
    // return Promise.resolve();
  }

  @override
  public get metadata() {
    const title = this.data.name;
    const description = this.data.description;
    const imageUrl = !!this.data.mainImage ? this.data.mainImage.url : null;

    return <PageMetadata>{
      title: title,
      description: description,
      imageUrl: imageUrl,
    };
  }

  @computed
  public get hasModels() {
    return this.data.models && this.data.models.any();
  }

  private getRequestQuoteQuery(productCode?: string, configurationCode?: string): ProductConfigurationQuery {
    switch (this.data.configurationMode) {
      case ProductConfigurationMode.Default: {
        if (configurationCode) {
          return { configurationCode };
        } else if (productCode) {
          return { productId: productCode };
        }
        break;
      }
      case ProductConfigurationMode.Modular: {
        return configurationCode ? { configurationCode } : { productFamilyId: this.familyId };
      }
      case ProductConfigurationMode.ModularExternal: {
        return { productFamilyId: this.familyId };
      }
      default:
        assertUnreachable(this.data.configurationMode);
    }
  }
}
